/**
 * The station creator's logic (the station workspaces plan, step 2: docs/plans/2026-10-06_station-workspaces.md,
 * "The station creator"), kept apart from the page so it is tested on its own. Pure.
 *
 * A station is a named group of plant-model equipment (Arjun's word). The creator must make the equipment that has data
 * easy to pick, because data arrives from sheets today and from gateways later, and a long list of machines with no data
 * hides the right ones. So:
 *   - Data first: the rows come from GET /v1/twin/stations/coverage, the machines with data first, then by process line
 *     (the plant model's lane order, no line last), then by name. A machine with no tag bound is greyed and hidden
 *     behind a switch that is off by default; a machine with tags but no recent data stays in view.
 *   - Find by data: the search reads a machine's name and the names of the tags bound to it, so "compactability" finds
 *     the muller that carries it. The coverage carries no tag names, so they come from the org's binding list.
 *   - Start from a line: a process line adds that line's machines with data to the selection, to adjust from there.
 *   - A starter set: an org with no station yet is offered one station per line that has data, named after the line.
 *
 * A row's line is the coverage's own when the entity stores one, else the plant model's derivation (lineForEntity, the
 * same lanes the process graph draws), else none. The STATION entity kind is a different thing from a station: a row
 * shows its kind through entityClassLabel, never as its code.
 */
import { PROCESS_LINE_INFO, lineForEntity, type EntityDef, type ProcessLine } from "@/foundry-widgets/foundry-plant";
import { errorCodeOf, errorDetailOf, statusOf } from "@/lib/api/error";
import type {
  Station,
  StationCoverageRow,
  StationCoverageSource,
  StationLine,
  StationMemberPut,
  StationPatch,
  StationRole,
} from "@/lib/api/stations";
import { formatDay, isPlantDay } from "@/lib/plant-days";

/** The plant model's process lines, in its lane order (melt first, tooling last). */
export const STATION_LINES: readonly ProcessLine[] = (Object.keys(PROCESS_LINE_INFO) as ProcessLine[]).sort(
  (a, b) => PROCESS_LINE_INFO[a].order - PROCESS_LINE_INFO[b].order,
);

/** Each line's short word, as a station is named after it ("Melt") and as the line select offers it. */
export const STATION_LINE_LABEL: Record<ProcessLine, string> = {
  melt: "Melt",
  sand: "Sand",
  core: "Core",
  mould: "Moulding",
  finishing: "Finishing",
  lab: "Lab",
  tooling: "Tooling",
};

/** The roles a person has at a station, in the order the select offers them, with their words. */
export const STATION_ROLES: readonly StationRole[] = ["operator", "lead", "engineer"];
export const STATION_ROLE_LABEL: Record<StationRole, string> = {
  operator: "Operator",
  lead: "Lead",
  engineer: "Engineer",
};

/** A station's name is 1 to 80 characters, trimmed (the server's rule, StationCreate). */
export const STATION_NAME_MAX = 80;

export function isProcessLine(s: string): s is ProcessLine {
  return Object.prototype.hasOwnProperty.call(PROCESS_LINE_INFO, s);
}

/** A line's word; "No line" for none, and a line the page does not know reads as itself. */
export function stationLineLabel(line: string): string {
  if (!line) return "No line";
  return isProcessLine(line) ? STATION_LINE_LABEL[line] : line;
}

/** One row of the creator: a coverage row with its line settled and the names of its tags. */
export interface CreatorRow extends StationCoverageRow {
  /** The coverage's own line when the entity stores one, else lineForEntity of its plant-model def, else null. */
  processLine: ProcessLine | null;
  /** The names of the tags bound to it, from the org's binding list (none while that list is not known). */
  tagNames: readonly string[];
}

/** A row's process line: the coverage's own when it is a line, else the plant model's derivation, else none. */
export function rowLineOf(coverageLine: string, def: EntityDef | undefined): ProcessLine | null {
  if (isProcessLine(coverageLine)) return coverageLine;
  return def ? lineForEntity(def) : null;
}

/** The names of the tags bound to each entity, each once, in the binding list's order. */
export function tagNamesByEntity(
  bindings: readonly { entity_id: string; tag_name: string }[],
): Map<string, string[]> {
  const byEntity = new Map<string, string[]>();
  for (const b of bindings) {
    if (!b.tag_name) continue;
    const names = byEntity.get(b.entity_id) ?? [];
    if (!names.includes(b.tag_name)) names.push(b.tag_name);
    byEntity.set(b.entity_id, names);
  }
  return byEntity;
}

const laneOrder = (line: ProcessLine | null) => (line ? PROCESS_LINE_INFO[line].order : STATION_LINES.length);

/** The creator's order: the machines with data first, then by process line (no line last), then by name. */
export function compareRows(a: CreatorRow, b: CreatorRow): number {
  if (a.has_data !== b.has_data) return a.has_data ? -1 : 1;
  const byLine = laneOrder(a.processLine) - laneOrder(b.processLine);
  if (byLine !== 0) return byLine;
  return (
    a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }) || a.entity_id.localeCompare(b.entity_id)
  );
}

/**
 * The creator's rows from the coverage, the plant model's nodes (for the lines the coverage leaves empty) and the
 * binding list (for the tag names; undefined while it is not known), in the creator's order.
 */
export function creatorRowsOf(
  coverage: readonly StationCoverageRow[],
  nodes: readonly { id: string; entity: EntityDef }[],
  bindings: readonly { entity_id: string; tag_name: string }[] | undefined,
): CreatorRow[] {
  const defs = new Map(nodes.map((n) => [n.id, n.entity]));
  const names = tagNamesByEntity(bindings ?? []);
  return coverage
    .map((row) => ({
      ...row,
      processLine: rowLineOf(row.line, defs.get(row.entity_id)),
      tagNames: names.get(row.entity_id) ?? [],
    }))
    .sort(compareRows);
}

/** A machine with no tag bound: greyed, and hidden until the switch shows it. */
export function isUntagged(row: Pick<StationCoverageRow, "tags">): boolean {
  return row.tags === 0;
}

/** Text as the search reads it: lower case, with a tag's underscores, dashes, dots and slashes read as spaces. */
export function findText(s: string): string {
  return s
    .toLowerCase()
    .replace(/[_\-./]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const findWords = (find: string) => findText(find).split(" ").filter(Boolean);

/** Whether a row answers the search: every word of it is in the machine's name or in a name of its tags. */
export function rowMatches(row: Pick<CreatorRow, "name" | "tagNames">, find: string): boolean {
  const words = findWords(find);
  if (words.length === 0) return true;
  const hay = [row.name, ...row.tagNames].map(findText).join("\n");
  return words.every((w) => hay.includes(w));
}

/** The tags of a row that hold a word of the search, so the row can say why it was found. None for an empty search. */
export function matchedTagNames(row: Pick<CreatorRow, "tagNames">, find: string): string[] {
  const words = findWords(find);
  if (words.length === 0) return [];
  return row.tagNames.filter((t) => words.some((w) => findText(t).includes(w)));
}

export interface VisibleOptions {
  /** "Show equipment without data": off by default. */
  showUntagged: boolean;
  find: string;
  /** The machines to keep in view: one with no tag stays listed once picked (or just unpicked), so a selection is never hidden. */
  selected: ReadonlySet<string>;
}

/** The rows the creator lists: the search's, and a machine with no tag only when the switch shows it or it is picked. */
export function visibleRows(rows: readonly CreatorRow[], opts: VisibleOptions): CreatorRow[] {
  return rows.filter(
    (r) => (opts.showUntagged || !isUntagged(r) || opts.selected.has(r.entity_id)) && rowMatches(r, opts.find),
  );
}

/** How many machines the switch hides from the search's rows (no tag, not picked). */
export function hiddenUntaggedCount(rows: readonly CreatorRow[], opts: VisibleOptions): number {
  if (opts.showUntagged) return 0;
  return rows.filter((r) => isUntagged(r) && !opts.selected.has(r.entity_id) && rowMatches(r, opts.find)).length;
}

/** Start from a line: the selection with that line's machines that have data added. It never takes one away. */
export function startFromLine(
  rows: readonly CreatorRow[],
  line: ProcessLine,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected);
  for (const r of rows) if (r.has_data && r.processLine === line) next.add(r.entity_id);
  return next;
}

/** One station of the starter set: a line, its name, and that line's machines with data. */
export interface StarterStation {
  line: ProcessLine;
  name: string;
  entities: string[];
}

/** The starter set: one station per line that has a machine with data, in lane order, named after the line. */
export function proposeStarterSet(rows: readonly CreatorRow[]): StarterStation[] {
  return STATION_LINES.flatMap((line) => {
    const entities = rows.filter((r) => r.has_data && r.processLine === line).map((r) => r.entity_id);
    return entities.length > 0 ? [{ line, name: STATION_LINE_LABEL[line], entities }] : [];
  });
}

/** The starter set is offered only to an org with no station yet, and only when some line has a machine with data. */
export function offersStarterSet(stations: readonly unknown[], proposal: readonly StarterStation[]): boolean {
  return stations.length === 0 && proposal.length > 0;
}

/** What the page knows when it decides whether the starter set is in view. */
export interface StarterView {
  canEdit: boolean;
  /** The creator's rows are built: the coverage is read and the plant model has loaded (its lines are final). */
  ready: boolean;
  /** The card is making stations, or keeps a refusal in view: the first station made would otherwise take it away. */
  held: boolean;
  /** The person closed the card: it stays away for this view. */
  dismissed: boolean;
  stations: readonly unknown[];
  proposal: readonly StarterStation[];
}

/** Whether the starter set is in view: offered to an org admin, held while it works, and gone once it is dismissed. */
export function showsStarterSet(v: StarterView): boolean {
  return v.canEdit && v.ready && !v.dismissed && (v.held || offersStarterSet(v.stations, v.proposal));
}

/** Where a machine's data comes from, in words: "Sheet: Sand log", "Gateway: Melt PLC", "Data entry". */
export function sourceWords(s: StationCoverageSource): string {
  // Read as a string, so a kind a later server adds still reads as its name.
  const kind: string = s.kind;
  if (kind === "sheet") return s.name ? `Sheet: ${s.name}` : "Sheet";
  if (kind === "gateway") return s.name ? `Gateway: ${s.name}` : "Gateway";
  if (kind === "manual") return s.name || "Data entry";
  if (kind === "computed") return s.name || "Computed tags";
  return s.name || kind;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A plant day as briefly as it reads: "6 Oct" in `thisYear`, "6 Oct 2025" in another. */
export function shortDay(day: string, thisYear: number): string {
  const y = Number(day.slice(0, 4));
  return y === thisYear ? `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1]}` : formatDay(day);
}

/**
 * A row's data, in words: "as of 6 Oct" for a machine with data; "no recent data" (with its last day, where one is
 * known) for a machine with tags whose data is old; "no data" for one with no tag.
 */
export function dataWords(
  row: Pick<StationCoverageRow, "tags" | "has_data" | "latest_day">,
  thisYear: number,
): string {
  const day = isPlantDay(row.latest_day) ? row.latest_day : null;
  if (row.has_data) return day ? `as of ${shortDay(day, thisYear)}` : "has data";
  if (row.tags === 0) return "no data";
  return day ? `no recent data, last ${shortDay(day, thisYear)}` : "no recent data";
}

/** A person as the members list names them: their name, else their email, else their id. */
export function personWords(p: { user_id: string; display_name?: string; email?: string }): string {
  return p.display_name?.trim() || p.email?.trim() || p.user_id;
}

/** What the editor holds while a person changes a station. */
export interface StationDraft {
  name: string;
  line: StationLine;
  entities: ReadonlySet<string>;
  /** Each member's role, by user id. */
  members: ReadonlyMap<string, StationRole>;
}

/** A station as the editor opens it. */
export function draftOf(station: Station): StationDraft {
  return {
    name: station.name,
    line: station.line,
    entities: new Set(station.entities),
    members: new Map(station.members.map((m) => [m.user_id, m.role])),
  };
}

/**
 * The draft an editor opens on. A station an org admin opens with a line and no equipment yet starts with that line's
 * machines with data picked (the plan: equipment with data is "selected by default where a station's process line
 * suggests it"); `preselected` names the line so the editor can say so. Nothing is written until the admin saves.
 */
export function openingDraft(
  station: Station,
  rows: readonly CreatorRow[] | undefined,
  canEdit: boolean,
): { draft: StationDraft; preselected: ProcessLine | null } {
  const draft = draftOf(station);
  if (!canEdit || !rows || station.entities.length > 0 || !isProcessLine(station.line)) {
    return { draft, preselected: null };
  }
  const entities = startFromLine(rows, station.line, draft.entities);
  return entities.size > 0 ? { draft: { ...draft, entities }, preselected: station.line } : { draft, preselected: null };
}

/** Why the draft cannot be saved, in words; null when it can. */
export function draftProblem(draft: Pick<StationDraft, "name">): string | null {
  const name = draft.name.trim();
  if (!name) return "A station needs a name.";
  if (name.length > STATION_NAME_MAX) return `A station's name is at most ${STATION_NAME_MAX} characters.`;
  return null;
}

/** The writes a save makes: each null when that part did not change. */
export interface StationSavePlan {
  patch: StationPatch | null;
  entities: string[] | null;
  members: StationMemberPut[] | null;
}

function sameSet(a: ReadonlySet<string>, b: readonly string[]): boolean {
  const bs = new Set(b);
  return a.size === bs.size && [...a].every((x) => bs.has(x));
}

function sameMembers(a: ReadonlyMap<string, StationRole>, b: Station["members"]): boolean {
  return a.size === b.length && b.every((m) => a.get(m.user_id) === m.role);
}

/**
 * The writes that take a station to the draft: PATCH its name and line where they changed, then the whole equipment set
 * and the whole members set, each only where it changed (each write is audited, so an unchanged set is not written).
 */
export function savePlanOf(station: Station, draft: StationDraft): StationSavePlan {
  const patch: StationPatch = {};
  const name = draft.name.trim();
  if (name !== station.name) patch.name = name;
  if (draft.line !== station.line) patch.line = draft.line;
  return {
    patch: Object.keys(patch).length > 0 ? patch : null,
    entities: sameSet(draft.entities, station.entities) ? null : [...draft.entities].sort(),
    members: sameMembers(draft.members, station.members)
      ? null
      : [...draft.members].map(([user_id, role]) => ({ user_id, role })),
  };
}

/** Whether a save would write anything. */
export function planWrites(plan: StationSavePlan): boolean {
  return plan.patch !== null || plan.entities !== null || plan.members !== null;
}

/** A refused or failed station write, in words. `doing` names the write: "Saving the station". */
export function stationErrorWords(err: unknown, doing: string): string {
  switch (errorCodeOf(err)) {
    case "station_name_taken":
      return "A station with that name already exists.";
    case "invalid_name":
      return `A station's name is 1 to ${STATION_NAME_MAX} characters.`;
    case "invalid_line":
      return "That is not one of the plant model's process lines.";
    case "unknown_entities":
      return "Some of that equipment is no longer in the plant model. Reload the page and pick again.";
    case "not_org_members":
      return "Some of those people are no longer members of the org. Reload the page and pick again.";
    case "station_not_found":
      return "That station is gone: someone deleted it. Reload the page.";
  }
  const status = statusOf(err);
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 403) return "Only an org admin can change stations.";
  if (status === 404) return "That station is gone: someone deleted it. Reload the page.";
  if (status === undefined) return `${doing} failed: the platform could not be reached. Try again.`;
  return errorDetailOf(err) ?? `${doing} failed (HTTP ${status}).`;
}
